import React, { useMemo, useState } from "react";
import { CalendarPlus } from "lucide-react";

import { Button } from "../../../../components/ui/button";
import WorkspaceCalendarGrid from "../WorkspaceCalendarGrid";
import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "../../projectionContract";
import ToolProjectionNotice from "./ToolProjectionNotice";
import { mayRender, resolveToolRenderStatus } from "./toolProjectionGuard";

const EMPTY_APPOINTMENTS = Object.freeze([]);


// Status chip colors reuse the SAME "disposition" palette already
// established for real appointment status display elsewhere in the
// application (getDispositionStyle, frontend/src/components/appointments/
// MiniCalendar.jsx) -- Scheduled = blue, Completed = purple -- rather than
// inventing new colors. Deliberately covers only the two statuses this
// prototype's approved vocabulary uses (WORKSPACE-AUDIT-005 SS11/SS17):
// "Scheduled" and "Completed" -- never the approved visual reference
// image's "Confirmed"/"Pending" mockup labels, which are not part of the
// real status domain.
const STATUS_CHIP_CLASS = {
  Scheduled: "bg-blue-500/20 text-blue-200 ring-1 ring-blue-400/30",
  Completed: "bg-purple-500/20 text-purple-200 ring-1 ring-purple-400/30",
};

function parseLocalDateString(dateString) {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function isSameLocalDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime(date) {
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// Duration is never a stored fixture field -- always derived from
// ends_at - starts_at, and omitted (not fabricated) when ends_at is
// absent (WORKSPACE-AUDIT-005 SS12/SS17 item 10).
function formatDuration(startsAt, endsAt) {
  if (!endsAt) return null;
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  return `${minutes} min`;
}

function formatScheduleSubtitle(date) {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function AppointmentRow({ appointment }) {
  const start = new Date(appointment.starts_at);
  const duration = formatDuration(appointment.starts_at, appointment.ends_at);
  const statusClass = STATUS_CHIP_CLASS[appointment.status] || "bg-white/10 text-slate-100 ring-1 ring-white/15";

  return (
    <div
      className="flex gap-2 border-b border-white/5 pb-2.5 last:border-b-0 last:pb-0"
      data-testid={`workspace-calendar-appointment-${appointment.id}`}
    >
      <div className="w-1 shrink-0 self-stretch rounded-full bg-indigo-500/60" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-slate-100">{formatTime(start)}</span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass}`}>
            {appointment.status}
          </span>
        </div>
        {duration ? <div className="text-[11px] text-slate-500">{duration}</div> : null}
        <div className="truncate text-xs font-semibold text-slate-100">{appointment.lead_name}</div>
        {appointment.title ? (
          <div className="truncate text-[11px] text-slate-400">{appointment.title}</div>
        ) : null}
        {appointment.phone ? (
          <div className="text-[11px] text-slate-500">{appointment.phone}</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Compact Agent Toolbar Calendar panel — a month calendar (react-day-picker,
 * imported directly rather than the shared components/ui/calendar.jsx
 * wrapper, whose path literally contains the word "calendar" and is
 * therefore blocked by the Agent Workspace Boundary-A safety scan — see
 * WORKSPACE-AUDIT-005 SS15) plus a Schedule list of the selected date's
 * appointments. Fixture/local-state driven, deterministic, clearly-
 * synthetic values only — no fetch, persistence, or API behavior.
 *
 * Architecture, per WORKSPACE-AUDIT-005:
 *   - Calendar date selection drives the Schedule below (Field Definition:
 *     selectedDate -> appointments filtered to that local calendar day,
 *     sorted chronologically by starts_at).
 *   - "Set Appt" is a separate, deliberately inert action in this pass —
 *     current Script Runner behavior already keeps its own Set Appt button
 *     independent of the selected calendar date, and WORKSPACE-AUDIT-005
 *     records that as CURRENT, INTENTIONAL product behavior, not a defect
 *     to fix. This prototype preserves that same independence rather than
 *     introducing a new coupling Script Runner itself doesn't have. It does
 *     not open any dialog, does not create fake persistence, and is not
 *     wired to any booking flow (no SetAppointmentDialog, no GHL/native
 *     booking integration — both explicitly out of scope for this pass).
 *   - Status vocabulary is restricted to "Scheduled"/"Completed" — the
 *     approved visual reference's "Confirmed"/"Pending" labels were visual
 *     mockup placeholders only, not real status-domain terminology
 *     (WORKSPACE-AUDIT-005 SS11/SS17).
 *   - No appointment-row click/navigation exists in this pass — Appointment
 *     Details navigation is an existing precedent elsewhere in the
 *     application, not part of this first Calendar prototype.
 *
 * Deliberately receives its selected date and appointment list as props
 * (defaulted from the fixture) rather than only reading the fixture module
 * internally, so a later Boundary-A-authorized real selector/hook can
 * supply `initialSelectedDate` and `appointments` in the same shape
 * without redesigning this presentation.
 *
 * The "Set Appt" button itself lives in `CalendarSetApptButton`, a named
 * export of this same module (co-located with the rest of the Calendar
 * tool's code) — the outer Agent Toolbar chrome (WorkspaceAgentToolsCard)
 * renders it in the shared title row when Calendar is the active tool, so
 * it appears on the same horizontal line as the "Calendar" heading rather
 * than inside this panel's own scrollable body. Its styling, icon, label,
 * and inert (no-op) behavior are unchanged by that move.
 */
export function CalendarSetApptButton() {
  return (
    <Button
      type="button"
      size="sm"
      className="h-7 gap-1 rounded-full px-3 text-[11px]"
      data-testid="workspace-calendar-set-appt-button"
    >
      <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
      Set Appt
    </Button>
  );
}

export default function CalendarToolPanel({
  initialSelectedDate,
  appointments,
  projectionStatus,
}) {
  // AW-ORIGINAL-RESTORE-001. Required, fail-closed.
  const renderStatus = resolveToolRenderStatus(projectionStatus, [
    initialSelectedDate,
    appointments,
  ]);
  const renderable = mayRender(renderStatus);

  // Seeded safely: `parseLocalDateString` would throw on an absent date,
  // and hooks run before the guard can return.
  const [selectedDate, setSelectedDate] = useState(() =>
    renderable ? parseLocalDateString(initialSelectedDate) : new Date()
  );

  const appointmentsForSelectedDate = useMemo(() => {
    if (!renderable) return EMPTY_APPOINTMENTS;
    return appointments
      .filter((appt) => isSameLocalDay(new Date(appt.starts_at), selectedDate))
      .slice()
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  }, [appointments, selectedDate, renderable]);

  if (!renderable) {
    return (
      <ToolProjectionNotice
        status={renderStatus.status}
        reason={renderStatus.reason}
        testIdPrefix="workspace-calendar"
      />
    );
  }

  const count = appointmentsForSelectedDate.length;
  const countLabel = `${count} ${count === 1 ? "Appt" : "Appts"}`;

  return (
    <div className="flex flex-col gap-3" data-testid="workspace-calendar-panel">
      {/*
        AW-TOOLS-003. The grid moved to the shared, fixture-free
        `WorkspaceCalendarGrid` so Production renders the same calendar
        from the same source. Same container, same `classNames` map,
        same chevrons, same testid -- only the file changed.
      */}
      <WorkspaceCalendarGrid selectedDate={selectedDate} onSelectDate={setSelectedDate} />

      <div className="flex flex-col gap-2" data-testid="workspace-calendar-schedule">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-slate-100">Schedule</p>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-indigo-300" data-testid="workspace-calendar-schedule-subtitle">
              {formatScheduleSubtitle(selectedDate)}
            </span>
            <span
              className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-slate-300"
              data-testid="workspace-calendar-schedule-count"
            >
              {countLabel}
            </span>
          </div>
        </div>

        {count === 0 ? (
          <p className="text-xs text-slate-500" data-testid="workspace-calendar-schedule-empty">
            No appointments scheduled.
          </p>
        ) : (
          <div className="flex flex-col gap-2.5">
            {appointmentsForSelectedDate.map((appointment) => (
              <AppointmentRow key={appointment.id} appointment={appointment} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
